import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http'
import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { TitleStrategy, provideRouter, withViewTransitions } from '@angular/router'
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core'
import Aura from '@primeng/themes/aura'
import { AuthModule, LogLevel } from 'angular-auth-oidc-client'
import { provideCharts, withDefaultRegisterables } from 'ng2-charts'
import { provideToastr } from 'ngx-toastr'
import { providePrimeNG } from 'primeng/config'
import { environment } from '../environments/environment'
import { appRoutes } from './app.routes'
import { apiAuthInterceptor } from './core/api-auth.interceptor'
import { MultiFileTranslateLoader } from './core/loaders/multi-file-translate-loader'
import { TranslatedTitleStrategy } from './core/translated-title.strategy'
import { errorTranslationInterceptor } from './shared/interceptor/error-translation.interceptor'
import { rateLimitInterceptor } from './shared/interceptor/rate-limit.interceptor'
import { refreshTokenInterceptor } from './shared/interceptor/refresh-token.interceptor'

export function MultiFileLoaderFactory(http: HttpClient) {
  return new MultiFileTranslateLoader(http, './assets/i18n')
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withViewTransitions({ skipInitialTransition: true })),
    provideHttpClient(withInterceptors([apiAuthInterceptor, errorTranslationInterceptor, refreshTokenInterceptor, rateLimitInterceptor])),
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
      closeButton: false,
      progressBar: true,
      enableHtml: false,
      tapToDismiss: true,
      toastClass: 'ngx-toastr',
      iconClasses: {
        error: 'toast-error',
        info: 'toast-info',
        success: 'toast-success',
        warning: 'toast-warning',
      },
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: MultiFileLoaderFactory,
        deps: [HttpClient],
      },
    }),
    provideAnimationsAsync(),
    provideCharts(withDefaultRegisterables()),
    providePrimeNG({
      theme: {
        preset: Aura,
        // Admin is light-only (TT-483). PrimeNG's default 'system' injects
        // `@media (prefers-color-scheme: dark) { :root { color-scheme: dark } }`
        // at runtime, which beats the `color-scheme: light` in styles.scss on
        // dark-OS machines: unstyled text turns near-white on our white cards.
        options: { darkModeSelector: 'none' },
      },
    }),
    importProvidersFrom(
      AuthModule.forRoot({
        config: {
          authority: environment.authAuthority,
          clientId: environment.authClientId,
          redirectUrl: environment.authRedirectUrl,
          postLogoutRedirectUri: window.location.origin,
          scope: 'openid profile email groups',
          responseType: 'code',
          useRefreshToken: true,
          silentRenew: true,
          silentRenewUrl: `${window.location.origin}/auth/silent-refresh.html`,
          renewTimeBeforeTokenExpiresInSeconds: 30,
          ignoreNonceAfterRefresh: true,
          logLevel: environment.production ? LogLevel.Error : LogLevel.Warn,
        },
      }),
    ),
  ],
}
